import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  podeEditar,
  podeGerenciarDispositivos,
  podeEditarProprioPainel,
  podeGerenciarPessoas,
  podeGerenciarWorkspaces,
  type Papel,
} from "@ghm/domain";

import { aoPerderSessao, ApiError, invalidateSessionRequests } from "@/lib/http";
import { stopPerformanceCapture } from "@/lib/performanceDiagnostics";
import { HistoryCache, HistoryCacheProvider } from "@/lib/HistoryCache";

import {
  buscarSessao,
  entrar as entrarNaApi,
  sair as sairDaApi,
  type EstadoAutenticacao,
  type UsuarioSessao,
} from "./api";

/**
 * Identidade da aba, resolvida UMA vez no boot.
 *
 * A sessao mora num cookie `httpOnly`: o front nao guarda token, nao renova
 * nada e nao sabe quando expira. Ele so pergunta ao servidor quem e — e o
 * servidor e a unica autoridade. Isso e o que torna a revogacao imediata:
 * apagar a linha em `sessions` derruba a pessoa no proximo pedido, sem esperar
 * um JWT vencer.
 *
 * Consequencia pratica: `permissoes` aqui e CONVENIENCIA DE INTERFACE, nao
 * seguranca. Esconder o botao de editar evita frustracao; quem realmente
 * recusa a edicao e `autorizar()` no servidor, que roda de novo a cada
 * requisicao.
 */
interface Permissoes {
  /** Identidade dos sinais e o que mais e coletivo. So `admin`. */
  editar: boolean;
  /** Mexer no PROPRIO painel: mover, redimensionar, criar grafico. Todos. */
  editarProprioPainel: boolean;
  gerenciarDispositivos: boolean;
  /** Cadastrar e listar quem tem acesso a organizacao. So `admin`. */
  gerenciarPessoas: boolean;
  /** Criar setor (workspace). So `admin`. Todo mundo LE a lista. */
  gerenciarWorkspaces: boolean;
}

interface AuthContextValue {
  /** `null` enquanto o boot ainda nao respondeu. */
  estado: EstadoAutenticacao | null;
  usuario: UsuarioSessao | null;
  papel: Papel | null;
  permissoes: Permissoes;
  /** `true` quando a API roda sem identidade (bancada). */
  aberto: boolean;
  entrar: (login: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
  /** Erro de infra no boot — API fora do ar, nao "nao logado". */
  erroDeBoot: string | null;
  /** Guarda de tarefas pessoais enfileiradas que sobrevivem a troca de rota. */
  isCurrentUser: (userId: string | undefined) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Sem identidade configurada, tudo e permitido: a bancada precisa editar.
 * Autenticado, o papel decide. Anonimo, nada — mas nesse caso a tela de login
 * ocupa o lugar do painel, entao ninguem consulta isto.
 */
function calcularPermissoes(estado: EstadoAutenticacao | null): Permissoes {
  if (estado?.modo === "desligada") {
    // Bancada nao tem organizacao nem pessoas — a tela nao existe la.
    return {
      editar: true,
      editarProprioPainel: true,
      gerenciarDispositivos: true,
      gerenciarPessoas: false,
      // Bancada nao tem organizacao dona de setor: a lista continua vindo do
      // `localStorage`, e criar la nao passa por servidor nenhum.
      gerenciarWorkspaces: true,
    };
  }
  if (estado?.modo === "autenticado") {
    return {
      editar: podeEditar(estado.usuario.papel),
      editarProprioPainel: podeEditarProprioPainel(estado.usuario.papel),
      gerenciarDispositivos: podeGerenciarDispositivos(estado.usuario.papel),
      gerenciarPessoas: podeGerenciarPessoas(estado.usuario.papel),
      gerenciarWorkspaces: podeGerenciarWorkspaces(estado.usuario.papel),
    };
  }
  return {
    editar: false,
    editarProprioPainel: false,
    gerenciarDispositivos: false,
    gerenciarPessoas: false,
    gerenciarWorkspaces: false,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoAutenticacao | null>(null);
  const [erroDeBoot, setErroDeBoot] = useState<string | null>(null);
  // Uma instancia por identidade: logout/login (mesmo usuario) nao herda cache.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const historyCache = useMemo(() => new HistoryCache(), [estado]);
  const historyCacheRef = useRef(historyCache);
  historyCacheRef.current = historyCache;
  const currentUserId = useRef<string | undefined>(undefined);
  const sessionInvalidated = useRef(false);
  currentUserId.current = estado?.modo === "autenticado" ? estado.usuario.id : undefined;
  const isCurrentUser = useCallback((id: string | undefined) => !sessionInvalidated.current &&
    id !== undefined && currentUserId.current === id, []);

  useEffect(() => {
    sessionInvalidated.current = false;
    const controller = new AbortController();
    void (async () => {
      try {
        setEstado(await buscarSessao(controller.signal));
        setErroDeBoot(null);
      } catch (erro) {
        if ((erro as { name?: string }).name === "AbortError") return;
        // 401 e resposta esperada, nao falha: significa "nao logado".
        if (erro instanceof ApiError && erro.status === 401) {
          setEstado({ modo: "anonimo" });
          setErroDeBoot(null);
          return;
        }
        // Qualquer outra coisa e infra. Distinguir importa: mostrar a tela de
        // login quando a API caiu faria a pessoa digitar a senha varias vezes
        // achando que errou.
        setEstado({ modo: "anonimo" });
        setErroDeBoot(
          erro instanceof ApiError && erro.isNetwork
            ? "Não foi possível falar com o servidor."
            : "O servidor respondeu com erro ao verificar a sessão.",
        );
      }
    })();
    return () => { sessionInvalidated.current = true; controller.abort(); };
  }, []);

  // Sessao que cai durante o uso derruba a aba para a tela de login. So vale
  // quando havia sessao: em modo bancada nao ha para onde cair, e um 401
  // qualquer nao pode trancar quem nunca precisou entrar.
  useEffect(() => {
    return aoPerderSessao(() => {
      stopPerformanceCapture();
      invalidateSessionRequests();
      historyCacheRef.current.clear();
      currentUserId.current = undefined;
      sessionInvalidated.current = true;
      setEstado((atual) => (atual?.modo === "autenticado" ? { modo: "anonimo" } : atual));
    });
  }, []);

  const entrar = useCallback(async (login: string, senha: string) => {
    historyCacheRef.current.clear();
    const usuario = await entrarNaApi(login, senha);
    invalidateSessionRequests();
    sessionInvalidated.current = false;
    setEstado({ modo: "autenticado", usuario });
    setErroDeBoot(null);
  }, []);

  const sair = useCallback(async () => {
    stopPerformanceCapture();
    invalidateSessionRequests();
    historyCacheRef.current.clear();
    currentUserId.current = undefined;
    sessionInvalidated.current = true;
    try {
      await sairDaApi();
    } finally {
      // Mesmo que o logout falhe na rede, a aba volta para a tela de login.
      // Deixar o painel aberto depois de a pessoa pedir para sair e pior do
      // que um cookie que sobrevive ate expirar.
      setEstado({ modo: "anonimo" });
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      estado,
      usuario: estado?.modo === "autenticado" ? estado.usuario : null,
      papel: estado?.modo === "autenticado" ? estado.usuario.papel : null,
      permissoes: calcularPermissoes(estado),
      aberto: estado?.modo === "desligada",
      entrar,
      sair,
      erroDeBoot,
      isCurrentUser,
    }),
    [estado, entrar, sair, erroDeBoot, isCurrentUser],
  );

  return <AuthContext.Provider value={value}><HistoryCacheProvider cache={historyCache}>
    {children}
  </HistoryCacheProvider></AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const contexto = useContext(AuthContext);
  if (!contexto) {
    throw new Error("useAuth precisa estar dentro de <AuthProvider>");
  }
  return contexto;
}
